import { format } from 'date-fns';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { KpiStatCard } from '@apps/web/components/admin/outreach_observability/kpi_stat_card';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    type JudgeStatsDay,
    type JudgeStatsSection,
    type OutreachTrendDay,
} from '@libraries/typescript/api_client';

// Shared humanizer for judge flag/grade slugs ("no-yes-reason" → "No yes
// reason"). Exported so the quality tab and flagged-runs table render labels
// identically without three private copies.
export function humanizeJudgeLabel(label: string): string {
    const text = label.split('-').join(' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
}

// Amber reads as "warning" and the app's --destructive as "serious": severity
// color rides ONLY the genuinely-bad grades. great/ok stay on calm chart
// tokens. amber-500 is inlined because the app has no amber design token.
const WARNING_COLOR = 'oklch(0.769 0.188 70.08)';

const GRADE_STYLES: ReadonlyArray<{
    readonly key: string;
    readonly label: string;
    readonly color: string;
}> = [
    { key: 'great', label: 'Great', color: 'var(--chart-2)' },
    { key: 'ok', label: 'OK', color: 'var(--chart-1)' },
    { key: 'bad', label: 'Bad', color: WARNING_COLOR },
    { key: 'urgent', label: 'Urgent', color: 'var(--destructive)' },
];

// Fixed color slots for flag-trend lines, assigned by a stable (alphabetical)
// key order so a flag keeps its hue across refreshes instead of being recolored
// by rank.
const FLAG_COLORS: ReadonlyArray<string> = [
    'var(--chart-1)',
    'var(--chart-2)',
    'var(--chart-3)',
    'var(--chart-4)',
    'var(--chart-5)',
];
const MAX_FLAG_LINES = 5;

type SeriesMeta = {
    readonly key: string;
    readonly label: string;
    readonly color: string;
};

function dayLabel(day: string): string {
    return format(new Date(`${day}T00:00:00`), 'M/d');
}

type TooltipEntry = {
    readonly name?: string;
    readonly value?: number;
    readonly color?: string;
    readonly dataKey?: string | number;
    // The full data row Recharts attaches to each entry; used to read
    // non-series fields (e.g. `judged`) that aren't rendered as their own bar.
    readonly payload?: { readonly judged?: number };
};

function SeriesTooltip({
    active,
    payload,
    label,
    valueFormatter,
    extraLine,
}: {
    readonly active?: boolean;
    readonly payload?: Array<TooltipEntry>;
    readonly label?: string;
    readonly valueFormatter: (value: number) => string;
    readonly extraLine?: (payload: Array<TooltipEntry>) => string | null;
}) {
    if (!active || !payload || payload.length === 0) {
        return null;
    }
    const extra = extraLine ? extraLine(payload) : null;
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{label}</div>
            {payload.map((entry) => (
                <div
                    key={entry.dataKey ?? entry.name}
                    className="flex items-center justify-between gap-4"
                >
                    <span className="flex items-center gap-1.5">
                        <span
                            className="inline-block size-2 rounded-sm"
                            style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-muted-foreground">
                            {entry.name}
                        </span>
                    </span>
                    <span className="tabular-nums">
                        {entry.value === undefined
                            ? '—'
                            : valueFormatter(entry.value)}
                    </span>
                </div>
            ))}
            {extra !== null ? (
                <div className="text-muted-foreground mt-1 border-t pt-1">
                    {extra}
                </div>
            ) : null}
        </div>
    );
}

function ChartEmpty({ height }: { readonly height: number }) {
    return (
        <div
            className="text-muted-foreground flex items-center justify-center rounded-md border text-sm"
            style={{ height }}
        >
            No data to plot.
        </div>
    );
}

function GradesChart({
    days,
    onSegmentClick,
}: {
    readonly days: Array<JudgeStatsDay>;
    readonly onSegmentClick?: (grade: string, day: string) => void;
}) {
    const presentGrades = new Set<string>();
    days.forEach((day) => {
        Object.entries(day.grade_counts).forEach(([grade, count]) => {
            if (count > 0) {
                presentGrades.add(grade);
            }
        });
    });
    const knownKeys = GRADE_STYLES.map((style) => style.key);
    const extras: Array<SeriesMeta> = [...presentGrades]
        .filter((grade) => !knownKeys.includes(grade))
        .sort()
        .map((grade) => ({
            key: grade,
            label: humanizeJudgeLabel(grade),
            color: 'var(--muted-foreground)',
        }));
    const series: Array<SeriesMeta> = [
        ...GRADE_STYLES.filter((style) => presentGrades.has(style.key)),
        ...extras,
    ];
    const rows = days.map((day) => ({
        label: dayLabel(day.day),
        // Raw ET date key kept on the row so a clicked segment can drill into
        // that day's judged runs.
        day: day.day,
        judged: day.judged_count,
        ...Object.fromEntries(
            series.map((meta) => [meta.key, day.grade_counts[meta.key] ?? 0]),
        ),
    }));
    if (rows.length === 0 || series.length === 0) {
        return <ChartEmpty height={288} />;
    }
    return (
        <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <RechartsTooltip
                        content={
                            <SeriesTooltip
                                valueFormatter={(value) => String(value)}
                                extraLine={(payload) => {
                                    // `judged` is a row field, not a rendered
                                    // series, so read it off the hovered row's
                                    // payload rather than searching for a bar.
                                    const judged = payload[0]?.payload?.judged;
                                    return judged === undefined
                                        ? null
                                        : `Judged: ${judged}`;
                                }}
                            />
                        }
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {series.map((meta) => (
                        <Bar
                            key={meta.key}
                            dataKey={meta.key}
                            name={meta.label}
                            stackId="grades"
                            fill={meta.color}
                            stroke="var(--background)"
                            strokeWidth={2}
                            isAnimationActive={false}
                            cursor={onSegmentClick ? 'pointer' : undefined}
                            onClick={
                                onSegmentClick
                                    ? (entry: {
                                          readonly payload?: {
                                              readonly day?: string;
                                          };
                                      }) => {
                                          const day = entry.payload?.day;
                                          if (day !== undefined) {
                                              onSegmentClick(meta.key, day);
                                          }
                                      }
                                    : undefined
                            }
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function CoverageChart({
    days,
    runsByDay,
}: {
    readonly days: Array<JudgeStatsDay>;
    readonly runsByDay: Map<string, number>;
}) {
    const rows = days.map((day) => {
        const runs = runsByDay.get(day.day) ?? 0;
        return {
            label: dayLabel(day.day),
            coverage: runs > 0 ? (day.judged_count / runs) * 100 : null,
        };
    });
    if (rows.length === 0) {
        return <ChartEmpty height={224} />;
    }
    return (
        <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rows} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis
                        tick={{ fontSize: 11 }}
                        width={44}
                        domain={[0, 100]}
                        tickFormatter={(value) => `${value}%`}
                    />
                    <RechartsTooltip
                        content={
                            <SeriesTooltip
                                valueFormatter={(value) =>
                                    `${value.toFixed(0)}%`
                                }
                            />
                        }
                    />
                    <Line
                        type="monotone"
                        dataKey="coverage"
                        name="% of runs judged"
                        stroke="var(--chart-1)"
                        strokeWidth={2}
                        dot={false}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function FlagTrendChart({
    days,
    onFlagClick,
}: {
    readonly days: Array<JudgeStatsDay>;
    readonly onFlagClick?: (flag: string) => void;
}) {
    const totals = new Map<string, number>();
    days.forEach((day) => {
        Object.entries(day.flag_counts).forEach(([flag, count]) => {
            totals.set(flag, (totals.get(flag) ?? 0) + count);
        });
    });
    const topFlags = [...totals.entries()]
        .filter(([, total]) => total > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, MAX_FLAG_LINES)
        .map(([flag]) => flag)
        // Alphabetical order fixes the color slot so a flag keeps its hue.
        .sort();
    const series: Array<SeriesMeta> = topFlags.map((flag, index) => ({
        key: flag,
        label: humanizeJudgeLabel(flag),
        color: FLAG_COLORS[index] ?? 'var(--muted-foreground)',
    }));
    const rows = days.map((day) => ({
        label: dayLabel(day.day),
        ...Object.fromEntries(
            series.map((meta) => [meta.key, day.flag_counts[meta.key] ?? 0]),
        ),
    }));
    if (rows.length === 0 || series.length === 0) {
        return <ChartEmpty height={224} />;
    }
    return (
        <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rows} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 11 }}
                        width={32}
                    />
                    <RechartsTooltip
                        content={
                            <SeriesTooltip
                                valueFormatter={(value) => String(value)}
                            />
                        }
                    />
                    <Legend
                        wrapperStyle={{
                            fontSize: 11,
                            cursor: onFlagClick ? 'pointer' : undefined,
                        }}
                        onClick={
                            onFlagClick
                                ? (entry: { readonly dataKey?: unknown }) => {
                                      if (typeof entry.dataKey === 'string') {
                                          onFlagClick(entry.dataKey);
                                      }
                                  }
                                : undefined
                        }
                    />
                    {series.map((meta) => (
                        <Line
                            key={meta.key}
                            type="monotone"
                            dataKey={meta.key}
                            name={meta.label}
                            stroke={meta.color}
                            strokeWidth={2}
                            dot={false}
                            connectNulls={false}
                            isAnimationActive={false}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

export function JudgeQualityPanel({
    judgeStats,
    trendDays,
    onGradeDayClick,
    onFlagClick,
}: {
    readonly judgeStats: JudgeStatsSection | null;
    readonly trendDays: Array<OutreachTrendDay>;
    // A grade segment on a given ET day was clicked (drills into judged runs).
    readonly onGradeDayClick?: (grade: string, day: string) => void;
    // A flag-trend legend entry was clicked (filters the flagged-runs table).
    readonly onFlagClick?: (flag: string) => void;
}) {
    if (judgeStats === null) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Judge quality (last 14 days)</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-muted-foreground py-6 text-center text-sm">
                        Loading judge stats…
                    </div>
                </CardContent>
            </Card>
        );
    }
    const days = judgeStats.days;
    const runsByDay = new Map(trendDays.map((day) => [day.day, day.num_runs]));
    const totalJudged = days.reduce((sum, day) => sum + day.judged_count, 0);
    const totalFlags = days.reduce(
        (sum, day) =>
            sum +
            Object.values(day.flag_counts).reduce(
                (acc, count) => acc + count,
                0,
            ),
        0,
    );
    const judgeCost = days.reduce((sum, day) => sum + day.judge_cost_usd, 0);
    const costPerJudged = totalJudged > 0 ? judgeCost / totalJudged : 0;
    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <KpiStatCard label="Judged (14d)" value={String(totalJudged)} />
                <KpiStatCard label="Flags (14d)" value={String(totalFlags)} />
                <KpiStatCard
                    label="Judge cost (14d)"
                    value={`$${judgeCost.toFixed(2)}`}
                />
                <KpiStatCard
                    label="Cost / judged run"
                    value={`$${costPerJudged.toFixed(3)}`}
                />
            </div>
            <Card>
                <CardHeader>
                    <CardTitle>Judge grades per day</CardTitle>
                    <CardDescription>
                        Runs judged each ET day by quality grade (last 14 days).
                        Amber and red mark the bad and urgent grades.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <GradesChart days={days} onSegmentClick={onGradeDayClick} />
                </CardContent>
            </Card>
            <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>Judged coverage</CardTitle>
                        <CardDescription>
                            Judged runs as a percentage of all outreach runs per
                            ET day (blank when a day had no runs).
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <CoverageChart days={days} runsByDay={runsByDay} />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle>Flag trends</CardTitle>
                        <CardDescription>
                            Daily counts for the five most-flagged categories in
                            the window.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <FlagTrendChart days={days} onFlagClick={onFlagClick} />
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
